import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { USD, fullDate, magnitude, stamp } from '../lib/format';
import { C, ink, typeStyle, zoneStyle } from '../lib/theme';
import type { InvoiceStatus } from '../lib/types';
import { Avatar } from './Avatar';
import { Drawer } from './Drawer';
import { ErrorBanner, Skeleton } from './primitives';

const INVOICE_TONE: Record<InvoiceStatus, { color: string; label: string; wash: string; border: string }> = {
  pago: {
    color: C.green, label: 'Pago', border: 'rgba(46,204,113,0.28)',
    wash: 'linear-gradient(100deg, rgba(46,204,113,0.08), rgba(253,250,249,0.02) 60%)',
  },
  pendente: {
    color: C.amber, label: 'Pendente', border: 'rgba(245,165,36,0.3)',
    wash: 'linear-gradient(100deg, rgba(245,165,36,0.09), rgba(253,250,249,0.02) 60%)',
  },
  falhou: {
    color: C.churn, label: 'Falhou', border: 'rgba(242,85,90,0.32)',
    wash: 'linear-gradient(100deg, rgba(242,85,90,0.1), rgba(253,250,249,0.02) 60%)',
  },
};

/**
 * Título de seção do drawer — reforçado (pedido explícito de otimizar os
 * títulos "da sessão de detalhamento e resumo dos clientes"): barra colorida
 * + fonte maior, contagem virou badge pílula (mesmo padrão de `Block` em
 * `primitives.tsx`) em vez de "· N" solto no texto.
 */
function Group({ label, count, children }: { label: string; count?: number; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
        <span style={{ width: 3, height: 14, borderRadius: 99, background: C.accent, flex: 'none' }} />
        <span style={{
          fontSize: 12.5, fontWeight: 800, letterSpacing: '0.045em', textTransform: 'uppercase',
          color: C.fg,
        }}>{label}</span>
        {count !== undefined && (
          <span style={{
            fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 99,
            background: `${C.accent}1c`, border: `1px solid ${C.accent}38`, color: C.accent,
            fontVariantNumeric: 'tabular-nums',
          }}>{count}</span>
        )}
      </div>
      {children}
    </section>
  );
}

/**
 * Client Drawer (PRD §7.4) — nível 3, contexto completo do cliente: contatos,
 * produtos ativos agrupados por zona, histórico de TODOS os eventos (auditados
 * ou não) e a linha do tempo de auditoria, que só aparece se houver ao menos um
 * evento já auditado.
 */
export function ClientDrawer({ masterId, onClose }: {
  masterId: string | null;
  onClose: () => void;
}) {
  const { data, loading, error, reload } = useAsync(
    () => (masterId ? api.clientDrawer(masterId) : Promise.resolve(null)),
    [masterId],
  );

  const zones = Array.from(new Set((data?.subscriptions ?? []).map((s) => s.zone).filter(Boolean))) as string[];
  const audited = (data?.events ?? []).filter((e) => e.audited_at);

  // Produtos ativos agrupados por zona (PRD §7.4).
  const byZone = new Map<string, typeof data extends null ? never : NonNullable<typeof data>['subscriptions']>();
  for (const s of data?.subscriptions ?? []) {
    const key = s.zone ?? '—';
    if (!byZone.has(key)) byZone.set(key, []);
    byZone.get(key)!.push(s);
  }

  return (
    <Drawer
      open={masterId !== null} onClose={onClose} title={masterId ?? ''}
      subtitle={
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          {data?.client.nome && <span style={{ fontSize: 12.5, color: ink(0.5) }}>{data.client.nome}</span>}
          {zones.map((z) => {
            const zs = zoneStyle(z);
            return (
              <span key={z} style={{
                fontSize: 11, fontWeight: 780, letterSpacing: '-0.005em',
                padding: '3px 11px', borderRadius: 99, background: zs.bg, color: zs.color,
                border: `1px solid ${zs.color}45`, boxShadow: `0 0 10px -3px ${zs.color}80`,
              }}>{z}</span>
            );
          })}
          {data?.client.responsavel_email && (
            <span
              title={`Responsável: ${data.client.responsavel_email}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 650,
                padding: '3px 10px 3px 3px', borderRadius: 99, background: ink(0.06), color: ink(0.68),
                border: `1px solid ${ink(0.1)}`,
              }}
            >
              <Avatar url={data.client.responsavel_avatar_url} email={data.client.responsavel_email} size={17} />
              responsável
            </span>
          )}
        </div>
      }
    >
      {loading && <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={64} />)}</div>}
      {error && <ErrorBanner message={error} onRetry={reload} />}

      {data && (
        <>
          <Group label="Contatos" count={data.contacts.length}>
            {data.contacts.length === 0 && <div style={{ fontSize: 12.5, color: ink(0.4) }}>Nenhum contato sincronizado.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {data.contacts.map((c, i) => (
                <div key={`${c.value}-${i}`} style={{
                  display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderRadius: 10,
                  background: ink(0.05), border: `1px solid ${ink(0.08)}`,
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', background: C.green, flex: 'none',
                    boxShadow: '0 0 8px rgba(95,227,154,0.6)',
                  }} />
                  <span style={{ fontSize: 12.5, color: ink(0.85), wordBreak: 'break-all', flex: 1 }}>{c.value}</span>
                  <span style={{ fontSize: 10.5, color: ink(0.32), textTransform: 'uppercase', letterSpacing: '0.06em', flex: 'none' }}>{c.contact_type}</span>
                </div>
              ))}
            </div>
          </Group>

          <Group label="Produtos ativos" count={data.subscriptions.length}>
            {data.subscriptions.length === 0 && <div style={{ fontSize: 12.5, color: ink(0.4) }}>Nenhuma assinatura ativa.</div>}
            {[...byZone.entries()].map(([zone, subs]) => {
              const zs = zoneStyle(zone);
              return (
                <div key={zone} style={{ marginBottom: 12, borderRadius: 13 }}>
                  <span style={{
                    fontSize: 11.5, fontWeight: 750, letterSpacing: '-0.005em',
                    padding: '4px 12px', borderRadius: 99, background: zs.bg, color: zs.color,
                    border: `1px solid ${zs.color}45`,
                  }}>{zone}</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 7 }}>
                    {subs.map((s, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10,
                        background: ink(0.04), border: `1px solid ${ink(0.08)}`,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 650, color: ink(0.88) }}>{s.product_category ?? '—'}</div>
                          <div style={{ fontSize: 11, color: ink(0.38), marginTop: 1 }}>
                            {[s.plan_frequency, s.status].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                        <div style={{
                          fontSize: 13, fontWeight: 700, color: ink(0.8), fontVariantNumeric: 'tabular-nums', flex: 'none',
                        }}>{USD(s.mrr_ativo)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </Group>

          <Group label="Histórico de eventos" count={data.events.length}>
            {data.events.length === 0 && <div style={{ fontSize: 12.5, color: ink(0.4) }}>Nenhum evento registrado.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {data.events.map((e) => {
                const t = typeStyle(e.event_type);
                return (
                  <div key={e.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10,
                    background: ink(0.04), border: `1px solid ${ink(0.08)}`, borderLeft: `2px solid ${t.color}`,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 650, color: ink(0.88) }}>
                        {e.event_type} · {e.zone ?? '—'}
                      </div>
                      <div style={{ fontSize: 11, color: ink(0.38), marginTop: 1 }}>
                        {fullDate(e.event_date)} · {e.audit_status === 'real_lost' ? 'Real Lost' : 'Em Análise'}
                        {e.reason_label ? ` · ${e.reason_label}` : ''}
                      </div>
                    </div>
                    <div style={{
                      fontSize: 13, fontWeight: 750, color: t.color, fontVariantNumeric: 'tabular-nums', flex: 'none',
                    }}>{USD(e.mrr_lost)}</div>
                  </div>
                );
              })}
            </div>
            {data.events.length > 0 && (
              <div style={{ fontSize: 11.5, color: ink(0.35), marginTop: 9 }}>
                Total perdido: <strong style={{ color: ink(0.6) }}>
                  {USD(data.events.reduce((s, e) => s + magnitude(e.mrr_lost), 0))}
                </strong> em {data.events.length} evento{data.events.length === 1 ? '' : 's'}
              </div>
            )}
          </Group>

          {/* Faturas (PRD §7.2): só status, data, valor, link — sem detalhe de
              cobrança, quem quiser o detalhe completo vai ao Stripe. */}
          <Group label="Faturas" count={data.invoices.length}>
            {data.invoices.length === 0 && <div style={{ fontSize: 12.5, color: ink(0.4) }}>Nenhuma fatura sincronizada.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {data.invoices.map((inv) => {
                const tone = INVOICE_TONE[inv.status_derivado];
                return (
                  <div key={inv.invoice_id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 13px', borderRadius: 11,
                    background: tone.wash, border: `1px solid ${tone.border}`, borderLeft: `3px solid ${tone.color}`,
                  }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', background: tone.color, flex: 'none',
                      boxShadow: `0 0 7px ${tone.color}99`,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 650, color: ink(0.88) }}>{tone.label}</div>
                      <div style={{ fontSize: 11, color: ink(0.38), marginTop: 1 }}>
                        {fullDate(inv.data_pagamento ?? inv.invoice_updated_at)}
                        {inv.motivo_falha ? ` · ${inv.motivo_falha}` : ''}
                      </div>
                    </div>
                    {inv.status_derivado === 'pago' && (
                      <div style={{
                        fontSize: 13, fontWeight: 700, color: ink(0.8), fontVariantNumeric: 'tabular-nums', flex: 'none',
                      }}>{USD(magnitude(inv.valor_pago_centavos) / 100)}</div>
                    )}
                    {inv.hosted_invoice_url && (
                      <a
                        href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                        title="Abrir fatura no Stripe"
                        style={{
                          width: 26, height: 26, borderRadius: 9, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', flex: 'none', fontSize: 12,
                          border: `1px solid rgba(74,125,255,0.3)`, background: 'rgba(74,125,255,0.13)', color: C.blueSoft,
                        }}
                      >
                        <svg viewBox="0 0 16 16" style={{
                          width: 11, height: 11, fill: 'none', stroke: 'currentColor', strokeWidth: 1.7,
                          strokeLinecap: 'round', strokeLinejoin: 'round', display: 'block',
                        }}>
                          <path d="M9.5 2.5h4v4" /><path d="M13.5 2.5 7 9" />
                          <path d="M11 9.5v3a1.5 1.5 0 0 1-1.5 1.5h-6A1.5 1.5 0 0 1 2 12.5v-6A1.5 1.5 0 0 1 3.5 5h3" />
                        </svg>
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </Group>

          {/* Só aparece se houver ao menos 1 evento auditado (PRD §7.4). Tag
              estilo Slack — o avatar de quem auditou substitui o pontinho
              genérico, pra "quem confirmou isso" ficar visível de relance. */}
          {audited.length > 0 && (
            <Group label="Linha do tempo de auditoria" count={audited.length}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingLeft: 2 }}>
                {audited.map((e, i) => (
                  <div key={e.id} style={{ display: 'flex', gap: 12, paddingBottom: i === audited.length - 1 ? 0 : 14 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none' }}>
                      {e.audited_by_email ? (
                        <span title={e.audited_by_email}>
                          <Avatar url={e.audited_by_avatar_url} email={e.audited_by_email} size={22} />
                        </span>
                      ) : (
                        <span style={{
                          width: 22, height: 22, borderRadius: '50%', background: ink(0.08),
                          border: `1px solid ${ink(0.12)}`,
                        }} />
                      )}
                      {i < audited.length - 1 && <span style={{ width: 1, flex: 1, background: ink(0.14), marginTop: 5 }} />}
                    </div>
                    <div style={{ minWidth: 0, paddingTop: 2 }}>
                      <div style={{ fontSize: 12.5, color: ink(0.85), fontWeight: 600 }}>
                        Real Lost{e.reason_label ? ` · ${e.reason_label}` : ''}
                      </div>
                      <div style={{ fontSize: 11, color: ink(0.35), marginTop: 1 }}>
                        {stamp(e.audited_at)}{e.audited_by_email ? ` · ${e.audited_by_email}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Group>
          )}
        </>
      )}
    </Drawer>
  );
}
